import React from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Typography } from '@kep-ui-kit/ui-components';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { withErrorHandling, IWrappedComponentProps } from '@selfserve/react-hocs';
import { useEmailResend, usePost } from '@selfserve/react-hooks';
import {
  StyledLogo,
  StyledV2PageWrapper,
  StyledV2LogoRow,
  StyledV2PageBackground,
  StyledV2GlassCard,
  StyledToast,
  StyledV2LinkButton,
  StyledV2MailIllustration,
  SniperLink,
} from '@selfserve/react-ui';
import { apiEndpoints, buttonIds } from '@selfserve/react-utils';

const useLoginByEmailRequest = () => usePost<any, any>(apiEndpoints.loginByEmail);

const StyledContentArea = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  alignItems: 'center',
  width: '100%',
  maxWidth: '720px',
  alignSelf: 'center',
}));

const StyledTextBlock = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  wordBreak: 'break-word',
}));

const CheckYourInboxLoginV2Component: React.FC<IWrappedComponentProps> = ({ openErrorModal }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const productFamily = searchParams.get('productFamily') || undefined;
  const { removeSuccessMessage, onClickHandle, successMessage, email } =
    useEmailResend<any, any>(openErrorModal, useLoginByEmailRequest, true, productFamily);

  return (
    <>
      {successMessage && (
        <StyledToast onClose={removeSuccessMessage} severity="success">
          <span>{successMessage}</span>
        </StyledToast>
      )}
      <Helmet>
        <title>{t('check-your-inbox-login-v2.check-your-inbox')}</title>
      </Helmet>
      <StyledV2PageWrapper>
        <StyledV2LogoRow>
          <StyledLogo />
        </StyledV2LogoRow>
        <StyledV2PageBackground centered className="container">
          <StyledV2GlassCard maxWidth="800px">
            <StyledContentArea>
              <StyledV2MailIllustration
                src="assets/check-your-inbox-v2-mail.svg"
                alt=""
              />
              <StyledTextBlock>
                <Typography variant="h1" sx={{ margin: 0, textAlign: 'center' }}>
                  {t('check-your-inbox-login-v2.check-your-inbox')}
                </Typography>
                <Typography variant="body2" textAlign={'center'}>
                  {t('check-your-inbox-login-v2.check-your-email-for-login')}
                </Typography>
                <Typography variant="body2" textAlign={'center'}>
                  {`${t('check-your-inbox-login-v2.cant-find-email')} `}
                  <StyledV2LinkButton
                    onClick={onClickHandle}
                    id={buttonIds.resendVerification}
                  >
                    {t('check-your-inbox-login-v2.resend')}
                  </StyledV2LinkButton>
                </Typography>
                <SniperLink email={email} />
              </StyledTextBlock>
            </StyledContentArea>
          </StyledV2GlassCard>
        </StyledV2PageBackground>
      </StyledV2PageWrapper>
    </>
  );
};

export const CheckYourInboxLoginV2 = withErrorHandling<unknown>(CheckYourInboxLoginV2Component);
